import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onDestroy } from 'svelte';
import { getShaderGenerator } from '../../../features/shader/shader-generator.ts';
import { getLanguage, subscribeLanguageChange, t } from '../i18n.ts';
import Button from './svelte-button.svelte';

var root = $.from_html(`<button type="button"> </button>`);
var root_1 = $.from_html(`<div class="shader-root svelte-1y9psr3"><div class="shader-header svelte-1y9psr3"><div><div class="section-label" id="shader-dialog-title"> </div> <div class="shader-help-text svelte-1y9psr3"> </div></div> <div class="shader-tabs svelte-1y9psr3"></div> <div class="shader-toolbar svelte-1y9psr3"><!> <!> <!></div></div> <div class="shader-meta svelte-1y9psr3"> </div> <pre class="shader-code-output svelte-1y9psr3"> </pre></div>`);

const $$css = {
	hash: 'svelte-1y9psr3',
	code: '.shader-root.svelte-1y9psr3 {display:flex;flex-direction:column;min-height:0;height:100%;}.shader-header.svelte-1y9psr3 {display:grid;grid-template-columns:repeat(3, 1fr);align-items:center;justify-content:space-between;gap:12px;padding:10px 12px;border-bottom:1px solid var(--color-line);}.shader-help-text.svelte-1y9psr3 {margin-top:4px;font-size:11px;color:var(--color-muted);}.shader-tabs.svelte-1y9psr3 {display:flex;align-items:center;gap:4px;margin-left:auto;margin-right:auto;padding:3px;border:1px solid var(--color-panel-border-strong, #464646);border-radius:999px;background:var(--color-tab-pill-bg, color-mix(in srgb, var(--color-panel), white 4%));}.shader-tab.svelte-1y9psr3 {border:1px solid transparent;background:transparent;border-radius:999px;padding:4px 10px;font-size:11px;color:var(--color-muted);cursor:pointer;}.shader-tab.svelte-1y9psr3:hover {transform:none;}.shader-tab-active.svelte-1y9psr3 {border-color:var(--color-accent);background:var(--color-accent);color:var(--color-accent-ink);}.shader-toolbar.svelte-1y9psr3 {display:flex;align-items:stretch;gap:8px;margin-left:auto;flex-wrap:wrap;justify-content:flex-end;}.shader-meta.svelte-1y9psr3 {padding:8px 12px;border-bottom:1px solid var(--color-line);font-family:var(--font-mono, monospace);font-size:11px;color:var(--color-muted);}.shader-code-output.svelte-1y9psr3 {flex:1;min-height:0;overflow:auto;padding:12px;font-family:var(--font-mono, monospace);font-size:11px;line-height:1.5;white-space:pre;color:var(--color-code-text, var(--color-text));user-select:text;margin:0;}\n\n  @media (max-width: 900px) {.shader-header.svelte-1y9psr3 {grid-template-columns:1fr;}.shader-toolbar.svelte-1y9psr3 {width:100%;justify-content:space-between;}\n  }'
};

export default function Svelte_shader_dialog($$anchor, $$props) {
	$.push($$props, true);
	$.append_styles($$anchor, $$css);

	const SHADER_CODE_ENTRIES = {
		'glsl-fragment': {
			label: 'GLSL Fragment',
			stageLabel: 'Fragment',
			language: 'glsl',
			getSource: (generator, input, cachedFragShader) => cachedFragShader ?? generator.buildFragment(input)
		},
		'glsl-vertex': {
			label: 'GLSL Vertex',
			stageLabel: 'Vertex',
			language: 'glsl',
			getSource: (generator) => {
				if (typeof generator.buildVertex !== 'function') {
					throw new Error('GLSL generator does not provide a vertex shader.');
				}

				return generator.buildVertex();
			}
		},
		'hlsl-fragment': {
			label: 'HLSL',
			stageLabel: 'HLSL',
			language: 'hlsl',
			getSource: (generator, input) => generator.buildFragment(input)
		},
		'mme-fragment': {
			label: 'MMEffect',
			stageLabel: 'MMEffect',
			language: 'mme',
			getSource: (generator, input) => generator.buildFragment(input)
		}
	};

	const buildInput = $.prop($$props, 'buildInput', 7, null),
		fragmentShader = $.prop($$props, 'fragmentShader', 7, undefined);

	function dispatch(eventName, detail) {
		$$props.$$host.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: true, composed: true }));
	}

	let language = $.state($.proxy(getLanguage()));
	let activeEntryId = $.state('glsl-fragment');

	const disposeLanguageSync = subscribeLanguageChange((nextLanguage) => {
		$.set(language, nextLanguage, true);
	});

	onDestroy(() => {
		disposeLanguageSync();
	});

	function tr(key, values) {
		$.get(language);

		return values ? t(key, values) : t(key);
	}

	function getActiveEntry() {
		return SHADER_CODE_ENTRIES[$.get(activeEntryId)] ?? SHADER_CODE_ENTRIES['glsl-fragment'];
	}

	function getShaderSource() {
		if (!buildInput()) {
			return '// Shader is not ready yet.';
		}

		const entry = getActiveEntry();

		return entry.getSource(getShaderGenerator(entry.language), buildInput(), fragmentShader());
	}

	function getMetaText() {
		const source = getShaderSource();

		return tr('shader.meta', {
			stage: getActiveEntry().stageLabel,
			lines: source.split('\n').length
		});
	}

	async function handleCopy() {
		if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
			dispatch('status-message', {
				message: tr('shader.status.clipboardUnavailable'),
				kind: 'error'
			});

			return;
		}

		try {
			await navigator.clipboard.writeText(getShaderSource());

			dispatch('status-message', {
				message: tr('shader.status.copySuccess', { stage: getActiveEntry().stageLabel }),
				kind: 'success'
			});
		} catch {
			dispatch('status-message', { message: tr('shader.status.copyFailed'), kind: 'error' });
		}
	}

	function handleExport() {
		dispatch('export-shader', { language: getActiveEntry().language });
	}

	var $$exports = {
		get buildInput() {
			return buildInput();
		},

		set buildInput($$value = null) {
			buildInput($$value);
			$.flush();
		},

		get fragmentShader() {
			return fragmentShader();
		},

		set fragmentShader($$value = undefined) {
			fragmentShader($$value);
			$.flush();
		}
	};

	var fragment = $.comment();
	var node = $.first_child(fragment);

	$.key(node, () => $.get(language), ($$anchor) => {
		var div = root_1();
		var div_1 = $.child(div);
		var div_2 = $.child(div_1);
		var div_3 = $.child(div_2);
		var text = $.only_child(div_3, true);
		var div_4 = $.sibling(div_3, 2);
		var text_1 = $.only_child(div_4, true);

		$.reset(div_2);

		var div_5 = $.sibling(div_2, 2);

		$.each(div_5, 21, () => Object.entries(SHADER_CODE_ENTRIES), ([id, entry]) => id, ($$anchor, $$item) => {
			var $$array = $.derived(() => $.to_array($.get($$item), 2));
			let id = () => $.get($$array)[0];
			let entry = () => $.get($$array)[1];
			var button = root();
			var text_2 = $.only_child(button, true);

			$.template_effect(() => {
				$.set_class(button, 1, `shader-tab ${$.get(activeEntryId) === id() ? 'shader-tab-active' : 'shader-tab-inactive'}`, 'svelte-1y9psr3');
				$.set_attribute(button, 'data-shader-stage', id());
				$.set_attribute(button, 'aria-pressed', $.get(activeEntryId) === id() ? 'true' : 'false');
				$.set_text(text_2, entry().label);
			});

			$.delegated('click', button, () => {
				$.set(activeEntryId, id(), true);
			});

			$.append($$anchor, button);
		});

		$.reset(div_5);

		var div_6 = $.sibling(div_5, 2);
		var node_1 = $.child(div_6);

		Button(node_1, {
			variant: 'secondary',
			handlePress: () => void handleCopy(),
			children: ($$anchor, $$slotProps) => {
				$.next();

				var text_3 = $.text();

				$.template_effect(($0) => $.set_text(text_3, $0), [() => tr('shader.copy')]);
				$.append($$anchor, text_3);
			},
			$$slots: { default: true }
		});

		var node_2 = $.sibling(node_1, 2);

		Button(node_2, {
			variant: 'submit',
			handlePress: () => void handleExport(),
			children: ($$anchor, $$slotProps) => {
				$.next();

				var text_4 = $.text();

				$.template_effect(($0) => $.set_text(text_4, $0), [() => tr('shader.download')]);
				$.append($$anchor, text_4);
			},
			$$slots: { default: true }
		});

		var node_3 = $.sibling(node_2, 2);

		{
			let $0 = $.derived(() => tr('shader.closeAria'));

			Button(node_3, {
				variant: 'secondary',
				get ariaLabel() {
					return $.get($0);
				},
				handlePress: () => dispatch('request-close'),
				children: ($$anchor, $$slotProps) => {
					$.next();

					var text_5 = $.text();

					$.template_effect(($0) => $.set_text(text_5, $0), [() => tr('shader.close')]);
					$.append($$anchor, text_5);
				},
				$$slots: { default: true }
			});
		}

		$.reset(div_6);
		$.reset(div_1);

		var div_7 = $.sibling(div_1, 2);
		var text_6 = $.only_child(div_7, true);
		var pre = $.sibling(div_7, 2);
		var text_7 = $.only_child(pre, true);

		$.reset(div);

		$.template_effect(
			($0, $1, $2, $3, $4) => {
				$.set_text(text, $0);
				$.set_text(text_1, $1);
				$.set_attribute(div_5, 'aria-label', $2);
				$.set_text(text_6, $3);
				$.set_text(text_7, $4);
			},
			[
				() => tr('shader.title'),
				() => tr('shader.help'),
				() => tr('shader.tabsAria'),
				() => getMetaText(),
				() => getShaderSource()
			]
		);

		$.append($$anchor, div);
	});

	$.append($$anchor, fragment);

	return $.pop($$exports);
}

$.delegate(['click']);
customElements.define('lut-shader-dialog-content', $.create_custom_element(Svelte_shader_dialog, { buildInput: {}, fragmentShader: {} }, [], []));